import { Calendar } from "@corpuscomplete/core-ui/common/calendar";
import { TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { useMemo } from "react";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { Plus } from "lucide-react";

/**
 * Timeline list renderer for date/time grouped items.
 * Contract: consumes normalized calendar items from @corpuscomplete/core-ui
 * and delegates row rendering to the provided renderItem callback.
 */
export function CalendarDateList<T extends Calendar.DateListItem>({
  items,
  timeZone,
  renderItem,
  onPressTimeAdd,
}: Calendar.DateListProps<T>) {
  const combined = useMemo(
    () => Calendar.combineItemsWithTimes(items, timeZone),
    [items, timeZone],
  );

  let lastTime: string | undefined;
  const timeWidthClass = "w-[40px]";

  return (
    <div className="flex flex-col gap-2">
      {combined.map((data) => {
        if (data.type === "time") {
          return (
            <div key={data.time.toString()} className="flex items-center">
              <div className={cn("flex flex-col items-center", timeWidthClass)}>
                <Text variant="xs">
                  {data.time.toLocaleString(undefined, {
                    hour: "numeric",
                  })}
                </Text>
              </div>
              {onPressTimeAdd && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label={`Add food at ${TemporalHelpers.formatTime(data.time)}`}
                  onClick={() => {
                    onPressTimeAdd(data.time);
                  }}
                >
                  <Plus className="size-3" />
                </Button>
              )}
              {/* TODO: Spot to render macro sum for all entries in the hour */}
            </div>
          );
        }
        const time = TemporalHelpers.formatTime(data.time);
        const isNewTime = time !== lastTime;
        if (isNewTime) {
          lastTime = time;
        }

        const timeWithoutAmPm = time.slice(0, -3);
        return (
          <div key={data.item.id} className="flex items-start gap-2">
            <div className={cn("flex flex-col items-center", timeWidthClass)}>
              {isNewTime && (
                <Text variant="xs" className="text-muted-foreground">
                  {timeWithoutAmPm}
                </Text>
              )}
            </div>
            <div className="min-w-0 flex-1">{renderItem(data.item)}</div>
          </div>
        );
      })}
    </div>
  );
}
